import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Radio, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { buildChartDataForMode } from './computeUsedLocationPercent';
import { MonthRangeSelect } from './MonthRangeSelect';
import { WarehouseLocationExplorer } from './WarehouseLocationExplorer';
import type {
  ChartYMode,
  MetricMode,
  MonthlyWarehouseMetric,
  PicqerLocation,
  PicqerLocationStockHistoryEntry,
  PicqerWarehouse,
  WarehouseSummaryRow,
} from './types';

const WAREHOUSE_COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2'];

interface UsedLocationsChartProps {
  warehouses: PicqerWarehouse[];
  chartData: Record<string, string | number>[];
  monthlyMetrics: MonthlyWarehouseMetric[];
  summaryRows: WarehouseSummaryRow[];
  metricMode: MetricMode;
  monthFrom: string;
  monthTo: string;
  onMonthRangeChange: (from: string, to: string) => void;
  locationsByWarehouse: Record<number, PicqerLocation[]>;
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>;
  preRangeStockByLocation: Record<number, number>;
  monthKeys: string[];
}

export const UsedLocationsChart: React.FC<UsedLocationsChartProps> = ({
  warehouses,
  chartData,
  monthlyMetrics,
  summaryRows,
  metricMode,
  monthFrom,
  monthTo,
  onMonthRangeChange,
  locationsByWarehouse,
  historyByLocation,
  preRangeStockByLocation,
  monthKeys,
}) => {
  const [hiddenWarehouseIds, setHiddenWarehouseIds] = useState<Set<number>>(new Set());
  const [chartYMode, setChartYMode] = useState<ChartYMode>('percent');
  const [expandedWarehouseId, setExpandedWarehouseId] = useState<number | null>(null);

  const visibleWarehouses = warehouses.filter((w) => !hiddenWarehouseIds.has(w.idwarehouse));

  const displayChartData = useMemo(
    () => buildChartDataForMode(chartData, warehouses, monthlyMetrics, chartYMode),
    [chartData, warehouses, monthlyMetrics, chartYMode],
  );

  const yMax = useMemo(() => {
    if (chartYMode === 'percent') return 100;
    let max = 0;
    for (const row of displayChartData) {
      for (const wh of visibleWarehouses) {
        const v = Number(row[`wh_${wh.idwarehouse}`] ?? 0);
        if (v > max) max = v;
      }
    }
    return Math.max(max * 1.1, 10);
  }, [displayChartData, visibleWarehouses, chartYMode]);

  const toggleWarehouse = (id: number) => {
    setHiddenWarehouseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const summaryColumns: ColumnsType<WarehouseSummaryRow> = [
    { title: 'Warehouse', dataIndex: 'warehouseName', key: 'warehouseName' },
    { title: 'Real locations', dataIndex: 'totalLocationsAll', key: 'totalLocationsAll' },
    { title: 'Max locations occupied', dataIndex: 'maxLocations', key: 'maxLocations' },
    { title: 'Max locations month', dataIndex: 'maxLocationsMonth', key: 'maxLocationsMonth' },
    {
      title: chartYMode === 'percent' ? 'Peak %' : 'Peak % (ref)',
      dataIndex: 'peakPercent',
      key: 'peakPercent',
      render: (v: number) => `${v.toFixed(1)}%`,
    },
    { title: 'Peak month', dataIndex: 'peakMonth', key: 'peakMonth' },
    {
      title: '',
      key: 'explore',
      render: (_: unknown, row: WarehouseSummaryRow) => (
        <Typography.Link
          onClick={() =>
            setExpandedWarehouseId((cur) => (cur === row.warehouseId ? null : row.warehouseId))
          }
        >
          {expandedWarehouseId === row.warehouseId ? 'Hide locations' : 'View locations'}
        </Typography.Link>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
        <Space wrap>
          <MonthRangeSelect monthFrom={monthFrom} monthTo={monthTo} onChange={onMonthRangeChange} />
          <Radio.Group
            value={chartYMode}
            onChange={(e) => setChartYMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="percent">% occupation</Radio.Button>
            <Radio.Button value="absolute">Locations occupied</Radio.Button>
          </Radio.Group>
        </Space>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Click a warehouse name in the legend below the chart to hide or show its line. Counts exclude
          empty parent/grouping nodes; parent locations with stock history are included.
        </Typography.Text>
      </Space>

      <div style={{ width: '100%', height: 400, marginBottom: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={displayChartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" angle={-45} textAnchor="end" height={70} />
            <YAxis
              domain={[0, yMax]}
              tickFormatter={(v) => (chartYMode === 'percent' ? `${v}%` : String(Math.round(v)))}
            />
            <Tooltip
              content={({ active, label }) => {
                if (!active || !label) return null;
                const row = displayChartData.find((r) => r.month === label);
                if (!row) return null;
                return (
                  <div
                    style={{
                      background: '#fff',
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      padding: '8px 12px',
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
                    {visibleWarehouses.map((wh, i) => {
                      const val = row[`wh_${wh.idwarehouse}`] ?? 0;
                      const used = row[`wh_${wh.idwarehouse}_used`] ?? 0;
                      const total = row[`wh_${wh.idwarehouse}_total`] ?? 0;
                      const pct = row[`wh_${wh.idwarehouse}_pct`] ?? 0;
                      return (
                        <div key={wh.idwarehouse} style={{ color: WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length] }}>
                          {wh.name}:{' '}
                          {chartYMode === 'absolute'
                            ? `${used} / ${total} locations`
                            : `${Number(pct).toFixed(1)}% avg`}
                          {chartYMode === 'absolute' && (
                            <span style={{ fontSize: 12, opacity: 0.85 }}> ({Number(pct).toFixed(1)}%)</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />
            <Legend
              onClick={(e) => {
                const wh = warehouses.find((w) => w.name === e.value);
                if (wh) toggleWarehouse(wh.idwarehouse);
              }}
              formatter={(value, entry) => {
                const wh = warehouses.find((w) => w.name === value);
                const hidden = wh && hiddenWarehouseIds.has(wh.idwarehouse);
                return (
                  <span style={{ opacity: hidden ? 0.35 : 1, cursor: 'pointer', textDecoration: hidden ? 'line-through' : 'none' }}>
                    {value}
                  </span>
                );
              }}
            />
            {warehouses.map((wh, i) => (
              <Line
                key={wh.idwarehouse}
                type="monotone"
                dataKey={`wh_${wh.idwarehouse}`}
                name={wh.name}
                stroke={WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length]}
                dot={false}
                strokeWidth={2}
                hide={hiddenWarehouseIds.has(wh.idwarehouse)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <Table
        rowKey="warehouseId"
        columns={summaryColumns}
        dataSource={summaryRows}
        pagination={false}
        size="small"
        style={{ marginBottom: 16 }}
      />

      {expandedWarehouseId != null && (
        <WarehouseLocationExplorer
          warehouse={warehouses.find((w) => w.idwarehouse === expandedWarehouseId)!}
          locations={locationsByWarehouse[expandedWarehouseId] ?? []}
          historyByLocation={historyByLocation}
          preRangeStockByLocation={preRangeStockByLocation}
          monthKeys={monthKeys}
          metricMode={metricMode}
          onClose={() => setExpandedWarehouseId(null)}
        />
      )}
    </div>
  );
};
